/**
 * Fragegruppen von Schritt 5 „Ansprache“ (Design: `aGroups`) — rein, ohne React.
 * Welche Gruppen erscheinen, hängt an Zielgruppe und Kontoart.
 */

import { asksInMailFallback, needsInMailCredit } from '../../campaign-wizard.logic';
import {
  Audience,
  PrimaryLanguage,
  RecruitsFor,
  SecondaryLanguage,
  WizardAccount,
  WizardState,
} from '../../campaign-wizard.types';

export type ApproachGroupId =
  | 'recruitsFor'
  | 'audience'
  | 'withNote'
  | 'inmailFallback'
  | 'formality'
  | 'referral'
  | 'inmailCredit';

export interface ApproachOption {
  value: RecruitsFor | Audience | boolean;
  labelKey: string;
  descKey?: string;
}

export interface ApproachGroup {
  id: ApproachGroupId;
  titleKey: string;
  options: ApproachOption[];
  /** CSS `grid-template-columns`. */
  columns: string;
}

const cols = (n: number): string => `repeat(${n},minmax(0,1fr))`;

export const buildApproachGroups = (
  state: Pick<WizardState, 'audience'>,
  productType: WizardAccount['productType'] | undefined
): ApproachGroup[] => {
  const groups: Omit<ApproachGroup, 'columns'>[] = [
    {
      id: 'recruitsFor',
      titleKey: 'wizApprRecruitsForTitle',
      options: [
        { value: 'own', labelKey: 'wizApprRecruitsOwn' },
        { value: 'clients', labelKey: 'wizApprRecruitsClients' },
      ],
    },
    {
      id: 'audience',
      titleKey: 'wizApprAudienceTitle',
      options: [
        { value: 'new', labelKey: 'wizApprAudienceNew', descKey: 'wizApprAudienceNewDesc' },
        {
          value: 'existing',
          labelKey: 'wizApprAudienceExisting',
          descKey: 'wizApprAudienceExistingDesc',
        },
        { value: 'both', labelKey: 'wizApprAudienceBoth', descKey: 'wizApprAudienceBothDesc' },
      ],
    },
  ];
  if (state.audience !== 'existing') {
    groups.push({
      id: 'withNote',
      titleKey: 'wizApprNoteTitle',
      options: [
        { value: true, labelKey: 'wizApprNoteWith', descKey: 'wizApprNoteWithDesc' },
        { value: false, labelKey: 'wizApprNoteWithout', descKey: 'wizApprNoteWithoutDesc' },
      ],
    });
  }
  // Recruiter / Sales Navigator: höchstens EINE InMail, wenn die Anfrage nicht angenommen wird – oder keine.
  if (asksInMailFallback(state, productType)) {
    groups.push({
      id: 'inmailFallback',
      titleKey: 'wizApprInmailTitle',
      options: [
        { value: true, labelKey: 'wizApprInmailWith', descKey: 'wizApprInmailWithDesc' },
        { value: false, labelKey: 'wizApprInmailWithout', descKey: 'wizApprInmailWithoutDesc' },
      ],
    });
  }
  groups.push({
    id: 'formality',
    titleKey: 'wizApprFormalityTitle',
    // true = Sie (Vorgabe neuer Kampagnen wie im alten Builder), false = Du
    options: [
      { value: true, labelKey: 'wizApprFormalSie', descKey: 'wizApprFormalSieDesc' },
      { value: false, labelKey: 'wizApprFormalDu', descKey: 'wizApprFormalDuDesc' },
    ],
  });
  groups.push({
    id: 'referral',
    titleKey: 'wizApprReferralTitle',
    options: [
      { value: true, labelKey: 'wizApprReferralYes', descKey: 'wizApprReferralYesDesc' },
      { value: false, labelKey: 'wizApprReferralNo', descKey: 'wizApprReferralNoDesc' },
    ],
  });
  if (needsInMailCredit(state, productType)) {
    groups.push({
      id: 'inmailCredit',
      titleKey: 'wizApprCreditTitle',
      options: [
        { value: true, labelKey: 'wizApprCreditYes', descKey: 'wizApprCreditYesDesc' },
        { value: false, labelKey: 'wizApprCreditNo', descKey: 'wizApprCreditNoDesc' },
      ],
    });
  }
  return groups.map(g => ({ ...g, columns: cols(g.options.length) }));
};

export const isOptionSelected = (
  state: WizardState,
  id: ApproachGroupId,
  value: ApproachOption['value']
): boolean => {
  switch (id) {
    case 'recruitsFor':
      return state.recruitsFor === value;
    case 'audience':
      return state.audience === value;
    case 'withNote':
      return state.withNote === value;
    case 'inmailFallback':
      return (state.inmailFallback !== false) === value;
    case 'formality':
      return (state.formalSpeech === true) === value;
    case 'referral':
      return state.referral === value;
    default:
      // Pflichtfrage: solange nichts gewählt ist, ist keine Option markiert.
      return state.inmailCredit === value;
  }
};

export const optionPatch = (
  id: ApproachGroupId,
  value: ApproachOption['value']
): Partial<WizardState> => {
  switch (id) {
    case 'recruitsFor':
      return { recruitsFor: value as RecruitsFor };
    case 'audience':
      return { audience: value as Audience };
    case 'withNote':
      return { withNote: value as boolean };
    case 'inmailFallback':
      return { inmailFallback: value as boolean };
    case 'formality':
      return { formalSpeech: value as boolean };
    case 'referral':
      return { referral: value as boolean };
    default:
      return { inmailCredit: value as boolean };
  }
};

export interface LanguageOption {
  value: string;
  labelKey: string;
}

export const PRIMARY_LANGUAGES: { value: PrimaryLanguage; labelKey: string }[] = [
  { value: 'de', labelKey: 'wizApprLangDe' },
  { value: 'en', labelKey: 'wizApprLangEn' },
  { value: 'auto', labelKey: 'wizApprLangAuto' },
];

const SECONDARY_LANGUAGES: { value: SecondaryLanguage; labelKey: string }[] = [
  { value: '', labelKey: 'wizApprLangNone' },
  { value: 'de', labelKey: 'wizApprLangDe' },
  { value: 'en', labelKey: 'wizApprLangEn' },
];

/** Sekundärsprachen ohne die gewählte Primärsprache. */
export const secondaryLanguageOptions = (primary: PrimaryLanguage) =>
  SECONDARY_LANGUAGES.filter(o => o.value !== primary);

/** Wechselt die Primärsprache; ist sie gleich der Sekundärsprache, wird diese zurückgesetzt. */
export const primaryLanguagePatch = (
  primary: PrimaryLanguage,
  secondary: SecondaryLanguage
): Partial<WizardState> =>
  secondary === primary ? { language: primary, language2: '' } : { language: primary };

export const TOGGLES = [
  { field: 'emojis', labelKey: 'wizApprEmojis' },
  { field: 'swissS', labelKey: 'wizApprSwissS' },
  { field: 'calendar', labelKey: 'wizApprCalendar', descKey: 'wizApprCalendarDesc' },
] as const;
